import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, Select, Typography, Snackbar, Alert } from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import api, { fetchCourses } from '../services/api';

const AddCourseDialog = ({
  open,
  onClose,
  departments,
  institutionId,
  onAdded,
  initialDeptId = '',
  onSuccess,
}) => {
  const [deptId, setDeptId] = useState('');
  const [shortName, setShortName] = useState('');
  const [longName, setLongName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [exampleCourse, setExampleCourse] = useState({ short: '', long: '', description: '' });
  const [courses, setCourses] = useState([]);
  const [shortNameError, setShortNameError] = useState('');
  const [longNameError, setLongNameError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (open) {
      setDeptId(initialDeptId || '');
      setShortName('');
      setLongName('');
      setDescription('');
      setShortNameError('');
      setLongNameError('');
    }
  }, [open, initialDeptId]);

  useEffect(() => {
    if (!open || typeof fetchCourses !== 'function') return;
    const result = fetchCourses(institutionId);
    if (result && typeof result.then === 'function') {
      result
        .then((res) => {
          const list = res.results || res;
          const first = list?.[0];
          setCourses(list || []);
          setExampleCourse({
            short: first?.short_name || '',
            long: first?.long_name || '',
            description: first?.description || '',
          });
        })
        .catch(() => {
          setCourses([]);
          setExampleCourse({ short: '', long: '', description: '' });
        });
    }
  }, [open, institutionId]);

  useEffect(() => {
    if (!shortName) {
      setShortNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.short_name?.toLowerCase().trim() ===
          shortName.trim().toLowerCase()
      );
      setShortNameError(dup ? 'Short name already exists' : '');
    }
  }, [shortName, courses]);

  useEffect(() => {
    if (!longName) {
      setLongNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.long_name?.toLowerCase().trim() ===
          longName.trim().toLowerCase()
      );
      setLongNameError(dup ? 'Long name already exists' : '');
    }
  }, [longName, courses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deptId || !shortName || !longName) return;
    setSubmitting(true);
    try {
      const payload = {
        department: deptId,
        short_name: shortName,
        long_name: longName,
        description,
        institution: institutionId,
      };
      await api.post('/courses/courses/', payload);
      const refreshed = await fetchCourses(institutionId);
      const list = refreshed.results || refreshed;
      onAdded(list);
      const msg = 'Course added';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
      setDeptId('');
      setShortName('');
      setLongName('');
      setDescription('');
      setShortNameError('');
      setLongNameError('');
    } catch (err) {
      console.error('Failed to add course:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ component: 'form', onSubmit: handleSubmit }}>
      <DialogTitle>Add Course</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <FormControl fullWidth>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select
            value={deptId}
            onChange={(e) => setDeptId(e.target.value)}
          >
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Short Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            fullWidth
            placeholder={exampleCourse.short ? `e.g. ${exampleCourse.short}` : ''}
          />
          {shortNameError && (
            <Typography variant="caption" color="error">
              {shortNameError}
            </Typography>
          )}
        </div>
        <div>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Long Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            value={longName}
            onChange={(e) => setLongName(e.target.value)}
            fullWidth
            placeholder={exampleCourse.long ? `e.g. ${exampleCourse.long}` : ''}
          />
          {longNameError && (
            <Typography variant="caption" color="error">
              {longNameError}
            </Typography>
          )}
        </div>
        <div>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Description <span style={{ opacity: 0.6 }}>(optional)</span>
          </Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder={exampleCourse.description ? `e.g. ${exampleCourse.description}` : ''}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={
            submitting ||
            !deptId ||
            !shortName ||
            !longName ||
            !!shortNameError ||
            !!longNameError
          }
          text="Add"
          color="info"
        />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddCourseDialog;
